import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export interface ConfirmDialogProps {
  title: string;
  body?: string;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  cancelLabel,
  danger = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;

    const focusable = () =>
      el.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
    focusable()[0]?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onCancel();
        return;
      }
      if (e.key !== "Tab") return;
      const all = focusable();
      if (all.length === 0) return;
      const first = all[0]!;
      const last = all[all.length - 1]!;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          last.focus();
          e.preventDefault();
        }
      } else if (document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  const accent = danger ? "var(--danger, #ef4444)" : "var(--primary)";

  return (
    <div
      ref={backdropRef}
      onClick={(e) => {
        if (e.target === backdropRef.current) onCancel();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 70,
        background: "rgba(15,26,48,.42)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        animation: "scRise .15s ease both",
      }}
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        style={{
          width: "100%",
          maxWidth: "380px",
          background: "var(--card)",
          borderRadius: "16px",
          padding: "22px 20px 18px",
          boxShadow: "0 8px 32px rgba(15,26,48,.22)",
        }}
      >
        <div style={{ font: "700 17px/1.3 var(--font-head)", letterSpacing: "-.01em" }}>
          {title}
        </div>
        {body && (
          <div style={{ fontSize: "13.5px", color: "var(--muted)", marginTop: "8px", lineHeight: 1.5 }}>
            {body}
          </div>
        )}

        <div style={{ display: "flex", gap: "8px", marginTop: "20px", justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            style={{
              height: "40px",
              padding: "0 16px",
              border: "1px solid var(--border)",
              borderRadius: "10px",
              background: "none",
              color: "var(--muted)",
              font: "600 13px var(--font-head)",
              cursor: "pointer",
            }}
          >
            {cancelLabel ?? t("cancel")}
          </button>
          <button
            onClick={onConfirm}
            style={{
              height: "40px",
              padding: "0 18px",
              border: "none",
              borderRadius: "10px",
              background: accent,
              color: "#fff",
              font: "600 13px var(--font-head)",
              cursor: "pointer",
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
